import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { Lock } from '@mui/icons-material';
import DeviceTypeImage from '../shared/DeviceTypeImage';
import { DeviceIconWithBrandChip } from '../shared/DeviceBrandChip';
import { SonosSpeaker } from '../../services/sonosApi';
import { useAiDevicePolicy, useShowAiCardBadge } from '../../contexts/AiDevicePolicyContext';
import AlbumArtImage from './AlbumArtImage';
import SonosCardMarquee from './SonosCardMarquee';
import CircularProgressPlayButton from './CircularProgressPlayButton';
import { getSonosCardMarqueeText } from './sonosCardMarquee';
import { useLiveTrackProgress } from './useLiveTrackProgress';
import { POWER_BUTTON_SIZE } from '../../styles/deviceStyles';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Misma altura fija que DeviceCard (Shelly): 64px. */
export const SONOS_CARD_HEIGHT = 64;

/** Tipografía del 3.er renglón (tipo de artefacto) — misma que DeviceCard/Shelly. */
export const artifactTypeLineSx = {
  fontSize: 10,
  color: '#cccccc',
  marginTop: 0.25,
  marginLeft: '0px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;
/**
 * Tapa del tema: cuadrada, casi el Ø del círculo de artefacto (60).
 * No más a la derecha: el mr del layout deja el hueco del play.
 */
export const SONOS_CARD_ALBUM_SIZE = 56;
/** Diámetro del play = PowerButton Shelly (43). */
export const SONOS_CARD_CONTROL_SIZE = POWER_BUTTON_SIZE;
/** Separación play ↔ borde derecho (igual que DeviceCard). */
export const SONOS_CARD_PLAY_RIGHT_PX = 10;
/**
 * Badges a la izquierda de la tapa (sin solaparse) y abajo del título del tema.
 * right = play + tapa + aire.
 */
export const SONOS_CARD_BADGE_RIGHT_PX =
  SONOS_CARD_PLAY_RIGHT_PX + SONOS_CARD_CONTROL_SIZE + 8 + SONOS_CARD_ALBUM_SIZE + 8;
export const SONOS_CARD_BADGE_BOTTOM_PX = 6;
/**
 * Misma ancla que DeviceCard → PowerButton:
 * right 10px + top 8px (no centrar con translate: eso lo desplazaba).
 */
export const SONOS_CARD_CONTROL_SX = {
  position: 'absolute' as const,
  right: `${SONOS_CARD_PLAY_RIGHT_PX}px`,
  top: '8px',
  width: SONOS_CARD_CONTROL_SIZE,
  height: SONOS_CARD_CONTROL_SIZE,
  minWidth: SONOS_CARD_CONTROL_SIZE,
  minHeight: SONOS_CARD_CONTROL_SIZE,
  flexShrink: 0,
  zIndex: 1,
  pointerEvents: 'auto' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box' as const,
};

export const sonosCardOuterSx = {
  backgroundColor: '#2F3235',
  display: 'flex',
  alignItems: 'center',
  padding: 1.25,
  borderRadius: 2,
  marginBottom: 0.5,
  height: SONOS_CARD_HEIGHT,
  width: '100%',
  boxSizing: 'border-box' as const,
  position: 'relative' as const,
  cursor: 'pointer',
  overflow: 'visible' as const,
};

/**
 * Badges (PIN / IA) en tarjetas Sonos:
 * a la izquierda de la tapa del tema y debajo del título que hace scroll.
 */
export const SonosCardAiBadge: React.FC<{
  speaker: SonosSpeaker;
  /** @deprecated conservado por compat */
  matchDeviceCard?: boolean;
  /** @deprecated conservado por compat */
  forceCentered?: boolean;
}> = ({ speaker }) => {
  const { t } = useTranslation();
  const showAiCardBadge = useShowAiCardBadge();
  const aiPolicy = useAiDevicePolicy();
  const aiWhitelisted = showAiCardBadge && aiPolicy.isSonosWhitelistedForIcon(speaker.id);
  const aiBlacklisted = showAiCardBadge && aiPolicy.isSonosBlacklistedForIcon(speaker.id);
  const pinRequired = Boolean(speaker.pin_required);
  if (!aiWhitelisted && !aiBlacklisted && !pinRequired) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        right: `${SONOS_CARD_BADGE_RIGHT_PX}px`,
        bottom: SONOS_CARD_BADGE_BOTTOM_PX,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '4px',
        zIndex: 5,
        pointerEvents: 'none',
      }}
    >
      {pinRequired && (
        <Lock
          sx={{
            fontSize: 13,
            color: '#2391FF',
          }}
        />
      )}
      {aiWhitelisted && (
        <Typography
          component="span"
          aria-label={t('deviceList.aiPolicyAllowed') as string}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.62rem',
            fontWeight: 800,
            color: '#66bb6a',
            minHeight: 14,
            lineHeight: '14px',
            letterSpacing: '-0.02em',
            userSelect: 'none',
          }}
        >
          {t('common.aiBadge')}
        </Typography>
      )}
      {aiBlacklisted && (
        <Typography
          component="span"
          aria-label={t('deviceList.aiPolicyDenied') as string}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.62rem',
            fontWeight: 800,
            color: '#ef5350',
            minHeight: 14,
            lineHeight: '14px',
            letterSpacing: '-0.02em',
            userSelect: 'none',
          }}
        >
          <Box
            component="span"
            sx={{
              textDecoration: 'line-through',
              textDecorationColor: '#ef5350',
              textDecorationThickness: '1.5px',
            }}
          >
            {t('common.aiBadge')}
          </Box>
        </Typography>
      )}
    </Box>
  );
};

interface SonosCardIconProps {
  speaker: SonosSpeaker;
  /** Chip de marca sobre el ícono (solo Settings modo edición). */
  showBrandChip?: boolean;
}

export const SonosCardIcon: React.FC<SonosCardIconProps> = ({ speaker, showBrandChip = false }) => (
  <DeviceIconWithBrandChip
    brand="sonos"
    showBrandChip={showBrandChip}
    sx={{ marginRight: 1 }}
  >
    <Box
      data-device-icon
      sx={{
        flexShrink: 0,
        touchAction: 'pan-y',
        pointerEvents: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <DeviceTypeImage
        tipo="SONOS"
        tipo_artefacto={speaker.artefacto?.nombre ?? null}
        iconOverride={speaker.artefacto?.icono ?? null}
        colorOverride={speaker.artefacto?.color_icono ?? null}
      />
    </Box>
  </DeviceIconWithBrandChip>
);

interface SonosCardPlaybackBodyProps {
  speaker: SonosSpeaker;
  canControl?: boolean;
  onTransport?: (speakerId: number, action: 'play' | 'pause') => void;
  showControl?: boolean;
  /**
   * Reservar la columna del play aunque esté oculto (modo edición Settings),
   * para que el lápiz quede en la misma X que en Shelly (antes del hueco del control).
   */
  reserveControlSpace?: boolean;
  nameSlot?: React.ReactNode;
}

export const SonosCardPlaybackBody: React.FC<SonosCardPlaybackBodyProps> = ({
  speaker,
  canControl = false,
  onTransport,
  showControl = true,
  reserveControlSpace = false,
  nameSlot,
}) => {
  const { playing, progress } = useLiveTrackProgress(speaker);
  const disabled = !canControl || !speaker.online;
  const keepControlColumn = showControl || reserveControlSpace;

  return (
    <>
      {/* Nombre / tema / tipo a la izquierda; tapa a la derecha (antes del play). */}
      <Box sx={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        transform: 'translateY(-2px)',
        mr: 0.75,
      }}>
        {nameSlot ?? (
          <Typography sx={{
            fontSize: 14,
            fontWeight: 'bold',
            color: speaker.online ? '#fff' : '#e57373',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
            mb: 0.15,
          }}>
            {speaker.nombre}
          </Typography>
        )}
        <SonosCardMarquee speaker={speaker} animating={playing} />
        {speaker.artefacto?.nombre ? (
          <Typography sx={artifactTypeLineSx}>
            {speaker.artefacto.nombre}
          </Typography>
        ) : null}
      </Box>
      <AlbumArtImage
        speakerId={speaker.id}
        artUrlKey={speaker.current_album_art_url}
        titleKey={speaker.current_title}
        artistKey={speaker.current_artist}
        fetchArt={Boolean(speaker.current_album_art_url || speaker.current_title)}
        size={SONOS_CARD_ALBUM_SIZE}
        borderRadius={1}
        sx={{
          flexShrink: 0,
          // Hueco del play (también en edición sin botón) → lápiz en la misma X que Shelly.
          mr: keepControlColumn ? `${SONOS_CARD_CONTROL_SIZE + 8}px` : 0.5,
        }}
      />
      {showControl && (
        <Box data-audio-player-control="" sx={SONOS_CARD_CONTROL_SX}>
          <CircularProgressPlayButton
            playing={playing}
            progress={progress}
            disabled={disabled}
            size={SONOS_CARD_CONTROL_SIZE}
            onClick={() => onTransport?.(speaker.id, playing ? 'pause' : 'play')}
          />
        </Box>
      )}
    </>
  );
};

export function useSonosCardState(speaker: SonosSpeaker, t: TranslateFn) {
  return {
    isPlaying: speaker.playback_state === 'playing',
    isPaused: speaker.playback_state === 'paused',
    secondLine: getSonosCardMarqueeText(speaker),
  };
}
